/**
 * usePresenceHeartbeat
 * Pings /api/presence/heartbeat every 30s while user is logged in,
 * and immediately when the route changes. Silent fail.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { getToken } from "../utils/tokenStorage";

const HEARTBEAT_INTERVAL_MS = 30000;

export default function usePresenceHeartbeat() {
  const location = useLocation();
  const intervalRef = useRef(null);
  const inflightRef = useRef(false);

  useEffect(() => {
    const send = async () => {
      // The auth token lives in sessionStorage (strict — no localStorage copy).
      // Read it fresh each tick via getToken so a login after mount starts
      // heartbeating without a reload. Reading the old localStorage key here is
      // exactly why live presence silently stopped updating.
      const token = getToken("aegis_token");
      if (!token || inflightRef.current) return;
      inflightRef.current = true;
      try {
        await fetch("/api/presence/heartbeat", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ page: location.pathname }),
          keepalive: true,
        });
      } catch (e) {
        // silent fail
      } finally {
        inflightRef.current = false;
      }
    };

    send();
    intervalRef.current = setInterval(send, HEARTBEAT_INTERVAL_MS);

    const onBeforeUnload = () => send();
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [location.pathname]);
}
